import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link as MuiLink
} from '@material-ui/core';
import { Visibility, VisibilityOff, CheckCircle } from '@material-ui/icons';
import swal from 'sweetalert';
import Api from '../utils/api';

const Register = () => {
  const navigate = useNavigate();
  const api = new Api();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (values, actions) => {
    actions.setSubmitting(true);

    try {
      const param = {
        name: values.name,
        email: values.email,
        password: values.password
      };

      await api.post('/users', param);

      await swal({
        title: 'Cadastro realizado com sucesso!',
        text: 'Sua conta foi criada. Agora você pode fazer login.',
        icon: 'success',
        button: 'Ir para Login'
      });

      navigate('/login');
    } catch (error) {
      console.error('Erro no cadastro:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.[0]?.message ||
        'Erro ao criar conta. Por favor, tente novamente.';

      swal({
        title: 'Erro no cadastro',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Cadastro | Sistema de Afiliados</title>
      </Helmet>
      <section
        style={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,.25), rgba(105, 80, 162, 0.5))',
            zIndex: 1
          }}
        />

        <div style={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100vw',
              justifyContent: 'center',
              alignItems: 'center',
              py: 3
            }}
          >
            <Container
              maxWidth="sm"
              sx={{ backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 2, p: 4, boxShadow: 5 }}
            >
              <Formik
                initialValues={{
                  name: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                  acceptTerms: false
                }}
                validationSchema={Yup.object().shape({
                  name: Yup.string().min(3, 'Nome deve ter no mínimo 3 caracteres').required('Nome é obrigatório'),
                  email: Yup.string().email('Email inválido').required('Email é obrigatório'),
                  password: Yup.string().min(6, 'Senha deve ter no mínimo 6 caracteres').required('Senha é obrigatória'),
                  confirmPassword: Yup.string()
                    .oneOf([Yup.ref('password'), null], 'As senhas não coincidem')
                    .required('Confirmação de senha é obrigatória'),
                  acceptTerms: Yup.boolean().oneOf([true], 'Você deve aceitar os termos e condições')
                })}
                onSubmit={handleSubmit}
              >
                {({ errors, handleBlur, handleChange, handleSubmit: formikHandleSubmit, isSubmitting, touched, values }) => (
                  <form onSubmit={formikHandleSubmit}>
                    <Box
                      sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        alt="Logo"
                        src="/static/logo.png"
                        style={{
                          width: 'auto',
                          height: 75,
                          resize: 'contain'
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography color="textPrimary" variant="h2">
                        Criar Conta
                      </Typography>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Cadastre-se para se tornar um afiliado
                      </Typography>
                    </Box>

                    <TextField
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      label="Nome completo"
                      margin="normal"
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      variant="outlined"
                      inputProps={{
                        style: {
                          backgroundColor: 'white'
                        }
                      }}
                    />

                    <TextField
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label="Email"
                      margin="normal"
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      value={values.email}
                      variant="outlined"
                      inputProps={{
                        style: {
                          backgroundColor: 'white'
                        }
                      }}
                    />

                    <TextField
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Senha"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      variant="outlined"
                      InputProps={{
                        style: {
                          backgroundColor: 'white'
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <TextField
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      fullWidth
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      label="Confirmar senha"
                      margin="normal"
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={values.confirmPassword}
                      variant="outlined"
                      InputProps={{
                        style: {
                          backgroundColor: 'white'
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.acceptTerms}
                          onChange={handleChange}
                          name="acceptTerms"
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Eu aceito os{' '}
                          <MuiLink href="#" underline="always">
                            termos e condições
                          </MuiLink>
                        </Typography>
                      }
                    />
                    {touched.acceptTerms && errors.acceptTerms && (
                      <Typography color="error" variant="caption" display="block">
                        {errors.acceptTerms}
                      </Typography>
                    )}

                    <Box sx={{ py: 2 }}>
                      <Button
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        startIcon={isSubmitting ? null : <CheckCircle />}
                      >
                        {isSubmitting ? 'Criando conta...' : 'Criar conta'}
                      </Button>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2">
                        Já tem uma conta?{' '}
                        <Link to="/login" style={{ color: '#5048E5', textDecoration: 'none', fontWeight: 600 }}>
                          Fazer login
                        </Link>
                      </Typography>
                    </Box>
                  </form>
                )}
              </Formik>
            </Container>
          </Box>
        </div>
      </section>
    </>
  );
};

export default Register;
